import React, { useEffect, useState } from 'react';
import { Menu, X, Mail, Home } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { LogoMark } from '../LogoMark';
const navItems = [{
  label: 'Browse All',
  path: '/inventory'
}, {
  label: 'Bags and Backpacks',
  path: '/inventory/bags-backpacks'
}, {
  label: 'Clothing',
  path: '/inventory/clothing'
}, {
  label: 'Fire Retardants',
  path: '/inventory/fire-retardants'
}, {
  label: 'Public Order / Control and Restraint',
  path: '/inventory/public-order'
}, {
  label: 'Tactical Vests',
  path: '/inventory/tactical-vests'
}, {
  label: 'Training Equipment',
  path: '/inventory/training-equipment'
}];
export function InventoryNavigation() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);
  const isActive = (path: string) => {
    if (path === '/' && location.pathname === '/') return true;
    if (path === '/inventory') {
      return location.pathname === '/inventory';
    }
    // Handle category paths and their children
    if (path.startsWith('/inventory/') && location.pathname.includes(path)) {
      return true;
    }
    if (path === '/contact' && location.pathname.startsWith('/contact')) return true;
    return false;
  };
  return <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled || isMobileMenuOpen ? 'bg-navy-900 shadow-md border-b border-white/5' : 'bg-navy-900/90 backdrop-blur-sm'}`} data-id="element-99">
      <div className="h-[72px] flex items-center" data-id="element-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full" data-id="element-101">
          <div className="flex items-center justify-between" data-id="element-102">
            {/* Logo */}
            <div className="flex-shrink-0" data-id="element-103">
              <Link to="/" className="text-white font-bold text-xl tracking-tight uppercase flex items-center gap-3" data-id="element-104">
                <LogoMark size="sm" variant="dark" data-id="element-105" />
                <span data-id="element-106">
                  <span className="text-cg-orange-600" data-id="element-107">Inventory</span> System
                </span>
              </Link>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-8" data-id="element-108">
              <Link to="/" className={`${isActive('/') ? 'text-cg-orange-600' : 'text-steel-300 hover:text-white'} text-sm font-semibold transition-colors duration-200`} data-id="element-109">
                Home
              </Link>

              <Link to="/inventory" className={`${isActive('/inventory') && location.pathname === '/inventory' ? 'text-cg-orange-600' : 'text-steel-300 hover:text-white'} text-sm font-semibold transition-colors duration-200`} data-id="element-110">
                Browse All
              </Link>

              <Link to="/contact" className={`${isActive('/contact') ? 'text-cg-orange-600' : 'text-steel-300 hover:text-white'} text-sm font-semibold transition-colors duration-200`} data-id="element-111">
                Contact / Support
              </Link>
            </div>

            {/* Mobile menu button */}
            <div className="lg:hidden" data-id="element-112">
              <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="inline-flex items-center justify-center p-2 rounded-md text-steel-300 hover:text-white focus:outline-none" data-id="element-113">
                <span className="sr-only" data-id="element-114">Open main menu</span>
                {isMobileMenuOpen ? <X className="block h-6 w-6" aria-hidden="true" data-id="element-115" /> : <Menu className="block h-6 w-6" aria-hidden="true" data-id="element-116" />}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && <div className="lg:hidden bg-navy-900 border-t border-white/10 absolute left-0 right-0 z-[60] shadow-xl max-h-[calc(100vh-72px)] overflow-y-auto" data-id="element-117">
          <div className="px-4 pt-4 pb-8 space-y-6" data-id="element-118">
            {/* Home Link (Top) */}
            <div data-id="element-119">
              <Link to="/" onClick={() => setIsMobileMenuOpen(false)} className={`flex items-center gap-3 px-3 py-3 rounded-md text-base font-medium transition-colors ${location.pathname === '/' ? 'text-cg-orange-600 bg-white/5' : 'text-white hover:bg-white/5'}`} data-id="element-120">
                <Home className="w-5 h-5" data-id="element-121" />
                Inventory Home
              </Link>
            </div>

            {/* Section 1: Categories */}
            <div className="pt-2 border-t border-white/10" data-id="element-122">
              <h3 className="text-xs font-bold text-steel-500 uppercase tracking-wider mb-3 px-3 mt-4" data-id="element-123">
                Categories
              </h3>
              <div className="space-y-1" data-id="element-124">
                {navItems.map(item => <Link key={item.path} to={item.path} onClick={() => setIsMobileMenuOpen(false)} className={`block px-3 py-3 rounded-md text-base font-medium transition-colors ${isActive(item.path) ? 'text-cg-orange-600 bg-white/5' : 'text-steel-300 hover:text-white hover:bg-white/5'}`} data-id="element-125">
                    {item.label}
                  </Link>)}
              </div>
            </div>

            {/* Section 2: Contact CTA */}
            <div className="pt-6 border-t border-white/10" data-id="element-126">
              <Link to="/contact" onClick={() => setIsMobileMenuOpen(false)} className="flex items-center justify-center gap-2 w-full bg-cg-orange-600 hover:bg-cg-orange-500 text-white font-bold uppercase tracking-wider py-3 px-4 rounded-sm text-sm transition-colors shadow-sm" data-id="element-127">
                <Mail className="w-4 h-4" data-id="element-128" />
                Contact Support
              </Link>
            </div>

            {/* Section 3: Legal */}
            <div data-id="element-129">
              <h3 className="text-xs font-bold text-steel-500 uppercase tracking-wider mb-3 px-3" data-id="element-130">
                Legal
              </h3>
              <div className="space-y-1" data-id="element-131">
                <Link to="/privacy-policy" onClick={() => setIsMobileMenuOpen(false)} className="block px-3 py-2 rounded-md text-sm font-medium text-steel-300 hover:text-white hover:bg-white/5 pl-6" data-id="element-132">
                  Privacy Policy
                </Link>
                <Link to="/terms-of-service" onClick={() => setIsMobileMenuOpen(false)} className="block px-3 py-2 rounded-md text-sm font-medium text-steel-300 hover:text-white hover:bg-white/5 pl-6" data-id="element-133">
                  Terms of Service
                </Link>
                <Link to="/cookie-policy" onClick={() => setIsMobileMenuOpen(false)} className="block px-3 py-2 rounded-md text-sm font-medium text-steel-300 hover:text-white hover:bg-white/5 pl-6" data-id="element-134">
                  Cookie Policy
                </Link>
              </div>
            </div>
          </div>
        </div>}
    </nav>;
}